/**
 * صفحه‌ی تأیید ایمیل — `/verify-email?token=…`
 *
 * عمداً بدون نیاز به ورود است: کاربر معمولاً لینک را روی دستگاهی باز
 * می‌کند که در آن وارد حساب نشده (مثلاً گوشی، از داخل برنامه‌ی ایمیل).
 * خودِ توکن تنها چیزی است که لازم است و پس از یک بار مصرف باطل می‌شود.
 */
import { useEffect, useRef, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { CheckCircle2, XCircle, Loader2, Mail } from 'lucide-react'
import { Shell } from '../components/Layout'
import { Card } from '../components/UI'
import { post } from '../lib/api'
import { useSeo } from '../lib/seo'

export default function VerifyEmail() {
  const [params] = useSearchParams()
  const token = params.get('token') || ''
  const [state, setState] = useState<'loading' | 'ok' | 'bad'>(token ? 'loading' : 'bad')
  const [message, setMessage] = useState('')
  const [email, setEmail] = useState('')
  // React در حالت توسعه افکت‌ها را دوبار اجرا می‌کند؛ توکن یک‌بارمصرف است،
  // پس اجرای دوم همیشه «نامعتبر» می‌گرفت و کاربر خطا می‌دید.
  const done = useRef(false)

  useSeo({ title: 'تأیید ایمیل', description: 'تأیید نشانی ایمیل حساب کاربری', noindex: true }, [])

  useEffect(() => {
    if (!token || done.current) return
    done.current = true
    post('/auth/email/verify/confirm', { token })
      .then((r: any) => { setState('ok'); setEmail(r?.email || '') })
      .catch((e: any) => { setState('bad'); setMessage(e?.message || 'این لینک نامعتبر یا منقضی شده است') })
  }, [token])

  return (
    <Shell>
      <div className="mx-auto flex max-w-lg flex-col items-center px-4 py-16 text-center">
        <Card className="w-full">
          {state === 'loading' && (
            <>
              <Loader2 className="mx-auto mb-4 h-8 w-8 animate-spin text-brand-300" />
              <h1 className="text-lg font-bold text-white">در حال بررسی…</h1>
            </>
          )}

          {state === 'ok' && (
            <>
              <CheckCircle2 className="mx-auto mb-4 h-10 w-10 text-up" />
              <h1 className="mb-2 text-lg font-bold text-white">ایمیل شما تأیید شد</h1>
              <p className="text-sm leading-8 text-slate-400">
                {email ? <span className="num" dir="ltr">{email}</span> : 'نشانی ایمیل شما'} با موفقیت تأیید شد.
                از این پس همه‌ی اطلاع‌رسانی‌ها را از طریق ایمیل هم دریافت می‌کنید.
              </p>
              <Link to="/dashboard" className="btn-primary mt-6 inline-flex">ورود به پنل</Link>
            </>
          )}

          {state === 'bad' && (
            <>
              <XCircle className="mx-auto mb-4 h-10 w-10 text-down" />
              <h1 className="mb-2 text-lg font-bold text-white">لینک معتبر نیست</h1>
              <p className="text-sm leading-8 text-slate-400">
                {message || 'نشانی تأیید ناقص است.'}
                <br />
                هر لینک فقط یک‌بار و تا مدت محدودی کار می‌کند. از پنل کاربری،
                بخش «امنیت و تنظیمات» می‌توانید لینک تازه بگیرید.
              </p>
              <Link to="/security" className="btn-ghost mt-6 inline-flex">
                <Mail className="h-4 w-4" /> ارسال دوباره لینک
              </Link>
            </>
          )}
        </Card>
      </div>
    </Shell>
  )
}
